import { Component, Input, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import * as rootReducer from './store';
import * as fromActions from './store/modal-delivery-share.actions';
import { ModalDeliveryShareModel, SharedReason } from '../../../../core/models';
import * as fromPhaseSelector from './store/modal-delivery-share.selectors';
import { VALIDATION_REGEXP } from '../../../constants/app-service-constants';

@Component({
  standalone: false,
  selector: 'pptl-delivery-share-view',
  templateUrl: './modal-delivery-share.component.html',
  styleUrls: ['./modal-delivery-share.component.scss'],
})
export class ModalDeliveryShareComponent implements OnDestroy {
  @Input() deliveryKeyCode: string;
  shareFormGroup: FormGroup;
  selectDropDownValid: boolean;
  isAgree: boolean;
  reasons$: Observable<SharedReason[]>;
  formInput: ModalDeliveryShareModel;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public modalController: ModalController,
    private formBuilder: FormBuilder,
    private store: Store<rootReducer.DeliveryShareState>,
  ) {
    this.reasons$ = this.store.pipe(select(fromPhaseSelector.getSharedReasons));
    this.store
      .pipe(select(fromPhaseSelector.getSharedForm), takeUntil(this.destroy$))
      .subscribe((data: ModalDeliveryShareModel) => {
        this.formInput = { ...data };
      });
    this.store.dispatch(fromActions.loadSharedReason());
    this.shareFormGroup = this.formBuilder.group({
      firstName: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(VALIDATION_REGEXP.AT_LEAST_ONE_LETTER),
        ]),
      ],
      lastName: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(VALIDATION_REGEXP.AT_LEAST_ONE_LETTER),
        ]),
      ],
      emailAddress: [
        '',
        Validators.compose([
          Validators.required,
          Validators.pattern(VALIDATION_REGEXP.SIMPLE_EMAIL),
        ]),
      ],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onCancelButtonClick(): void {
    this.modalController.dismiss();
  }

  onSendButtonClick() {
    const deliveryShare = {
      ...this.shareFormGroup.value,
      distributionKeyCode: this.deliveryKeyCode,
      reasonForSharing: this.formInput.reasonForSharing,
      userEvent: 'PORTAL_DELIVERY_SHARE',
    };
    this.store.dispatch(fromActions.sendDelivery({ deliveryShare }));
    this.modalController.dismiss();
  }

  isError(controlName: string, errorType: string): boolean {
    const control: AbstractControl = this.shareFormGroup.get(controlName);
    const controlHasError = control.hasError(errorType);

    return controlHasError && (control.dirty || control.touched);
  }

  getClass(controlName: string, errorType: string): string {
    return this.isError(controlName, errorType) || this.isError(controlName, 'required')
      ? 'error'
      : 'valid';
  }

  selected(ev): void {
    this.formInput.reasonForSharing = ev;
  }

  isValid(valid: boolean): void {
    this.selectDropDownValid = valid;
  }

  toggleCheckbox(): void {
    this.isAgree = !this.isAgree;
  }

  isInvalidForm(): boolean {
    return !this.shareFormGroup.valid || !this.selectDropDownValid || !this.isAgree;
  }
}
